import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { useTranslation } from "react-i18next";
import Alert from "react-bootstrap/esm/Alert";
import Button from "react-bootstrap/esm/Button";
import Card from "react-bootstrap/esm/Card";
import Spinner from "react-bootstrap/esm/Spinner";
import { functions } from "../services/firebase";

type BackfillResult = { applied: boolean; text: string };

/**
 * One-off choir data backfill (functions/src/choirBackfill.ts): first a dry
 * run that only reports, then – after reading the report – the real run.
 */
function ChoirBackfill() {
    const { t } = useTranslation();
    const [running, setRunning] = useState<"check" | "apply" | null>(null);
    const [result, setResult] = useState<BackfillResult | null>(null);
    const [error, setError] = useState("");

    const run = async (apply: boolean) => {
        if (apply && !window.confirm(String(t("superadmin.choirBackfillConfirm")))) return;
        setRunning(apply ? "apply" : "check");
        setError("");
        try {
            const backfill = httpsCallable<{ apply: boolean }, BackfillResult>(functions, "backfillChoirPerformances", {
                timeout: 540000,
            });
            setResult((await backfill({ apply })).data);
        } catch (err: any) {
            setError(err?.message || t("errors.anErrorOccurred"));
        } finally {
            setRunning(null);
        }
    };

    return (
        <Card className="mb-4">
            <Card.Body>
                <h4>{t("superadmin.choirBackfillTitle")}</h4>
                <p className="text-muted">{t("superadmin.choirBackfillHint")}</p>
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <Button variant="primary" disabled={running !== null} onClick={() => run(false)}>
                        {running === "check" && <Spinner animation="border" size="sm" className="me-2" />}
                        {t("superadmin.choirBackfillCheck")}
                    </Button>
                    {/* Writing is only offered after a dry run has been looked at. */}
                    <Button
                        variant="outline-danger"
                        disabled={running !== null || !result || result.applied}
                        onClick={() => run(true)}
                    >
                        {running === "apply" && <Spinner animation="border" size="sm" className="me-2" />}
                        {t("superadmin.choirBackfillApply")}
                    </Button>
                </div>
                {error && <Alert variant="danger">{error}</Alert>}
                {result && (
                    <>
                        <Alert variant={result.applied ? "success" : "info"}>
                            {t(result.applied ? "superadmin.choirBackfillApplied" : "superadmin.choirBackfillDryRun")}
                        </Alert>
                        <pre className="border rounded p-3 small" style={{ whiteSpace: "pre-wrap", maxHeight: "60vh", overflow: "auto" }}>
                            {result.text}
                        </pre>
                    </>
                )}
            </Card.Body>
        </Card>
    );
}

export default ChoirBackfill;
